import type { CellViewWire } from '@badger-sentinel/protocol';
import { useEffect, useMemo, useRef } from 'react';

import { FacetRow } from './FacetRow.js';
import { RailFrame } from './RailFrame.js';
import { RegionSection } from './RegionSection.js';
import { RosterRowView } from './RosterRow.js';
import { naturalSlotFor, targetForSubject } from '../model/builder.js';
import { armedCount, facetsOf, filterRoster } from '../model/filter.js';
import { toDrafts } from '../model/queue.js';
import { rosterOf, type RosterGroup, type RosterRow } from '../model/roster.js';
import type { Selection } from '../model/selection.js';
import { useConsoleStore } from '../state/store.js';

/**
 * The left rail — search, facets, five roster groups, regions (design 175–340).
 *
 * Presentational (M5-D2): it takes a `CellViewWire` and emits callbacks, and it
 * reads the console store for the interaction state that must survive a snapshot
 * swap. No fetch, no socket — the container is `SeatConsole` and stays the only
 * place with I/O.
 *
 * ## Counting
 *
 * A group header carries the number of rows it is showing and nothing else. Not
 * "5 of 12", not a total beside a filtered figure — the copy rules forbid the
 * phrasing and must-not #1 forbids any count of what the viewer was not given.
 */

export interface RosterRailProps {
  readonly view: CellViewWire | null;
  /** Both from `railLayout`, computed once by the shell against the live viewport. */
  readonly width: number;
  readonly maxWidth: number;
  readonly onContextMenu: (row: RosterRow, x: number, y: number) => void;
  readonly onRegionContextMenu: (region: string, x: number, y: number) => void;
}

export function RosterRail(props: RosterRailProps): JSX.Element {
  const open = useConsoleStore((state) => state.railLeftOpen);
  const toggleRail = useConsoleStore((state) => state.toggleRail);
  const setRailWidth = useConsoleStore((state) => state.setRailWidth);
  const search = useConsoleStore((state) => state.search);
  const setSearch = useConsoleStore((state) => state.setSearch);
  const filterOpen = useConsoleStore((state) => state.filterOpen);
  const setFilterOpen = useConsoleStore((state) => state.setFilterOpen);
  const facets = useConsoleStore((state) => state.facets);
  const cycleFacet = useConsoleStore((state) => state.cycleFacet);
  const clearFacets = useConsoleStore((state) => state.clearFacets);
  const closedGroups = useConsoleStore((state) => state.closedGroups);
  const toggleGroup = useConsoleStore((state) => state.toggleGroup);
  const regionsOpen = useConsoleStore((state) => state.regionsOpen);
  const setRegionsOpen = useConsoleStore((state) => state.setRegionsOpen);
  const selection = useConsoleStore((state) => state.selection);
  const select = useConsoleStore((state) => state.select);
  const pins = useConsoleStore((state) => state.pins);
  const batch = useConsoleStore((state) => state.batch);
  const toggleBatch = useConsoleStore((state) => state.toggleBatch);
  const queue = useConsoleStore((state) => state.queue);
  const fillSlot = useConsoleStore((state) => state.fillSlot);
  const fillTarget = useConsoleStore((state) => state.fillTarget);
  const showToast = useConsoleStore((state) => state.showToast);
  // The reciprocal of the map's `bs-hover`: one key, so the rail and the canvas
  // ring the same unit whichever one the cursor is on (design 2119–2137).
  const hoverId = useConsoleStore((state) => state.hoverId);
  const setHover = useConsoleStore((state) => state.setHover);

  const searchRef = useRef<HTMLInputElement>(null);

  // `/` focuses the search box (design's placeholder says so, and the hotkey map
  // lists it). Bound here rather than in the shell because this is where the
  // target is; a key bound to a box that may not be mounted is the bug Phase 1
  // avoided by binding nothing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof HTMLElement) {
        if (target.isContentEditable) return;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      }
      event.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const roster = useMemo(
    () => (props.view === null ? [] : rosterOf(props.view, toDrafts(queue))),
    [props.view, queue],
  );
  const allRows = useMemo(() => roster.flatMap((group) => group.rows), [roster]);
  const facetList = useMemo(() => facetsOf(allRows, facets), [allRows, facets]);
  const shown = useMemo(() => filterRoster(roster, search, facets), [roster, search, facets]);
  const armed = armedCount(facets);

  const pinned = useMemo(() => new Set(pins.map((pin) => pin.id)), [pins]);
  const batched = useMemo(() => new Set(batch), [batch]);

  // ⇧ takes the slot the row naturally belongs in; ⌥ forces the target slot.
  // The refusal is a toast rather than a silent no-op: a modifier-click that
  // does nothing at all is indistinguishable from a console that is not
  // listening.
  const view = props.view;
  const fillNatural = (id: string): void => {
    if (view === null) return;
    if (naturalSlotFor(view, id) === 'asset') {
      fillSlot('asset', id);
      return;
    }
    fillAsTarget(id);
  };
  const fillAsTarget = (id: string): void => {
    if (view === null) return;
    const target = targetForSubject(view, id);
    if (target === null) showToast(id + ' CANNOT BE A TARGET', 'warn');
    else fillTarget(target);
  };

  return (
    <RailFrame
      side="left"
      label="FORCES"
      open={open}
      width={props.width}
      maxWidth={props.maxWidth}
      onToggle={() => toggleRail('left')}
      onResize={(next) => setRailWidth('left', next)}
    >
      <FacetRow
        search={search}
        onSearch={setSearch}
        filterOpen={filterOpen}
        onToggleFilter={() => setFilterOpen(!filterOpen)}
        facets={facetList}
        armed={armed}
        onCycle={cycleFacet}
        onClear={clearFacets}
        searchRef={searchRef}
      />

      <div data-testid="roster" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {shown.map((group) => (
          <Group
            key={group.id}
            group={group}
            open={!closedGroups.has(group.id)}
            onToggle={() => toggleGroup(group.id)}
            selection={selection}
            onSelect={select}
            pinned={pinned}
            batched={batched}
            onToggleBatch={toggleBatch}
            onContextMenu={props.onContextMenu}
            onFillNatural={fillNatural}
            onFillTarget={fillAsTarget}
            hovered={hoverId}
            onHover={setHover}
          />
        ))}

        {props.view !== null && (
          <RegionSection
            view={props.view}
            open={regionsOpen}
            onToggle={() => setRegionsOpen(!regionsOpen)}
            selection={selection}
            onSelect={select}
            onContextMenu={props.onRegionContextMenu}
            search={search}
          />
        )}

        {props.view !== null && shown.length === 0 && <Empty text="NO MATCH" />}
        {props.view === null && <Empty text="NO PICTURE" />}
      </div>
    </RailFrame>
  );
}

function Group(props: {
  readonly group: RosterGroup;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly selection: Selection | null;
  readonly onSelect: (selection: Selection) => void;
  readonly pinned: ReadonlySet<string>;
  readonly batched: ReadonlySet<string>;
  readonly onToggleBatch: (id: string) => void;
  readonly onContextMenu: (row: RosterRow, x: number, y: number) => void;
  readonly onFillNatural: (id: string) => void;
  readonly onFillTarget: (id: string) => void;
  readonly hovered: string | null;
  readonly onHover: (id: string | null) => void;
}): JSX.Element {
  return (
    <div data-testid="roster-group" data-group={props.group.id}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 'var(--z-rail-sticky)',
          background: 'var(--surface-3)',
          borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border-strong)',
        }}
      >
        <button
          type="button"
          data-testid={'roster-group-toggle-' + props.group.id}
          aria-expanded={props.open}
          onClick={props.onToggle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            height: 32,
            padding: '0 12px',
            background: 'transparent',
            border: 0,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              flex: 'none',
              fontFamily: 'var(--font-data)',
              fontSize: 12,
              color: 'var(--text-3)',
              transform: props.open ? 'rotate(90deg)' : 'none',
            }}
          >
            ›
          </span>
          <span
            style={{
              flex: 1,
              fontFamily: 'var(--font-ui)',
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text)',
            }}
          >
            {props.group.label}
          </span>
          <span
            data-testid={'roster-group-count-' + props.group.id}
            style={{
              flex: 'none',
              fontFamily: 'var(--font-data)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-2)',
            }}
          >
            {props.group.rows.length}
          </span>
        </button>
      </div>

      {props.open &&
        props.group.rows.map((row) => (
          <RosterRowView
            key={row.key}
            row={row}
            selected={
              props.selection !== null &&
              row.selects !== null &&
              props.selection.kind === row.selects.kind &&
              props.selection.id === row.selects.id
            }
            pinned={props.pinned.has(row.id)}
            batched={props.batched.has(row.id)}
            onSelect={props.onSelect}
            onToggleBatch={props.onToggleBatch}
            onContextMenu={props.onContextMenu}
            onFillNatural={props.onFillNatural}
            onFillTarget={props.onFillTarget}
            hovered={props.hovered === row.id}
            onHover={props.onHover}
          />
        ))}
    </div>
  );
}

function Empty(props: { readonly text: string }): JSX.Element {
  return (
    <div
      data-testid="roster-empty"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'var(--font-data)',
        fontSize: 11,
        letterSpacing: '.14em',
        color: 'var(--text-muted)',
      }}
    >
      {props.text}
    </div>
  );
}
